import { createSelector } from 'reselect';
import quicksort from 'quicksort-js';
import get from 'lodash/get';
import unique from 'lodash/uniq';

import Render from 'redux/models/RenderV2';

import { getStateSliceFor } from './index';
import { appDataReducerKey } from './reducer';
import { getUserId } from 'redux/User';

/** Returns the app data state slice. */
const extractFrom = state => getStateSliceFor({ state, reducerKey: appDataReducerKey });

/** Returns the matched router URL params. */
export const getRouterParams = (state) => {
  return extractFrom(state).routerParams || {};
};

/** Returns the active spaceId. */
export const getActiveSpaceId = (state) => {
  return getRouterParams(state).spaceId || null;
};

/** Returns the active designId. */
export const getActiveDesignId = (state) => {
  return getRouterParams(state).designId || null;
};

/** Returns the active productId. */
export const getActiveProductId = (state) => {
  return getRouterParams(state).productId || null;
};

/** Returns the space for the provided id. */
export const getSpaceFor = ({ spaceId, state }) => {
  const data = extractFrom(state);
  const space = data.spaces[spaceId];
  return space || null;
};

/** Returns the active space. */
export const getActiveSpace = ({ state }) => {
  const spaceId = getActiveSpaceId(state);
  return getSpaceFor({ spaceId, state });
};

export const getCurrentActiveSpace = () => (dispatch, getState) => {
  return getActiveSpace({ state: getState() });
};

/** Returns all of the loaded spaces. */
export const getAllSpacesFrom = createSelector(
  state => {
    const data = extractFrom(state);
    return data ? data.spaces : {};
  },
  (spaceMap) => {
    const spaces = Object.keys(spaceMap).map(spaceId => spaceMap[spaceId]);
    return spaces;
  }
);

/** Returns all PPO spaces for logged in user from loaded spaces. */
const getAllPPOSpacesFrom = createSelector(
  state => getAllSpacesFrom(state),
  state => getUserId(state),
  (spaces, userId) => spaces.filter(space => (
    space.isPostPurchase() && space.userId() === userId
  ))
);

/** Returns all Studio spaces for logged in user from loaded spaces. */
export const getAllStudioSpacesFrom = createSelector(
  state => getAllSpacesFrom(state),
  state => getUserId(state),
  (spaces, userId) => spaces.filter(space => (
    space.isStudio() && space.userId() === userId
  ))
);

/** Returns all of a active space's user's spaces. */
export const getAllOfUsersSpacesFrom = createSelector(
  ({ state }) => getAllSpacesFrom(state),
  ({ userId }) => userId,
  (spaces, userId) => {
    return spaces.filter(space => space.userId() === userId);
  }
);

/** Returns true if user has at least one space that is ppo or studio. */
export const hasAPurchasedSpace = createSelector(
  state => getAllPPOSpacesFrom(state),
  state => getAllStudioSpacesFrom(state),
  (ppoSpaces, studioSpaces) => (ppoSpaces.length + studioSpaces.length) > 0
);

/** Returns latest purchased space. */
export const getLatestPurchasedSpace = createSelector(
  state => getAllPPOSpacesFrom(state),
  state => getAllStudioSpacesFrom(state),
  (ppoSpaces, studioSpaces) => {
    const spaces = studioSpaces.length ? studioSpaces : ppoSpaces;
    return spaces.sort((a, b) => new Date(b.orderDate()) - new Date(a.orderDate()))[0];
  }
);

/** Returns the active design. */
export const getActiveDesign = ({ state }) => {
  const designId = getActiveDesignId(state);
  return getDesignFor({ designId, state });
};

export const getCurrentActiveDesign = () => (dispatch, getState) => {
  return getActiveDesign({ state: getState() });
};

/** Returns the design for the provided id. */
export const getDesignFor = ({ designId, state }) => {
  const data = extractFrom(state);
  const design = data.designs[designId];
  return design || null;
};

export const getCurrentDesignFor = ({ designId }) => (dispatch, getState) => {
  return getDesignFor({ designId, state: getState() });
};

/** Returns all the designs for the provided space id. */
export const getActiveSpaceDesigns = createSelector(
  (state) => getActiveSpace({ state }),
  (state) => extractFrom(state).designs,
  (space, designData) => {
    const designs = space.designIds()
      .map(designId => designData[designId])
      .filter(design => !!design);

    return designs;
  }
);

/** Returns the product for the provided id. */
export const getProductFor = ({ productId, state }) => {
  const data = extractFrom(state);
  const product = data.products[productId] || null;
  return product;
};

export const getCurrentProductFor = ({ productId }) => (dispatch, getState) => {
  return getProductFor({ productId, state: getState() });
};

/**
 * @param {Array} productIds
 * @param {Object} state
 * @return {Array} products
 */
export const getBatchProductsByIds = createSelector(
  ({ productIds }) => productIds,
  ({ state }) => getIndexedProducts(state),
  (productIds, allProducts) => {
    const products = productIds
      .map(productId => allProducts[productId])
      .filter(product => !!product);
    return products;
  }
);

/** Returns the active product. */
export const getActiveProduct = ({ state }) => {
  const productId = getActiveProductId(state);
  return getProductFor({ productId, state });
};

/** Returns all the products for the provided design id. */
export const getProductsFor = ({ designId, state }) => {
  const design = getDesignFor({ designId, state });
  const productIds = design.fields.productIds;
  const products = productIds.map(productId => getProductFor({ productId, state }));
  return products;
};

/** Returns all the cached variant ids for the variant hash */
export const getProductVariantIds = ({ variantHash, state }) => {
  const allVariantIds = getVariants(state);
  return get(allVariantIds, variantHash, []);
};

/** Returns all the products in the active studio. */
export const getStudioProducts = createSelector(
  ({ state }) => getActiveSpace({ state }),
  ({ state }) => extractFrom(state).products,
  (space, products) => {
    const studioProducts = getIsSharedSpace()
      ? Object.keys(products).map(id => products[id])
      : space.productIds()
        .map(id => products[id])
        .filter(prod => !!prod);

    return studioProducts;
  }
);

/** Returns all the products in the active studio and active design. */
export const getActiveDesignProducts = createSelector(
  ({ state }) => getActiveSpace({ state }),
  ({ state }) => extractFrom(state).products,
  ({ state }) => getActiveDesign({ state }),
  (space, products, activeDesign) => {
    if (!activeDesign) { return []; }

    const studioProducts = getIsSharedSpace()
      ? Object.keys(products).map(id => products[id])
      : space.productIds()
        .map(id => products[id])
        .filter(prod => !!prod && activeDesign.productIds().includes(prod.id()));

    return studioProducts;
  }
);

/** Returns all the products currently in state indexed by ID. */
export const getIndexedProducts = state => {
  return extractFrom(state).products;
};

/** Returns the map for product variants in redux */
export const getVariants = state => {
  return extractFrom(state).productVariants;
};

export const getPriceRange = createSelector(
  ({state}) => extractFrom(state).products,
  (products) => {
    let productArray = Object.values(products);
    let minPrice = -1;
    let maxPrice = -1;

    productArray.forEach(product => {
      if (product.currentPrice() < minPrice || minPrice < 0) { minPrice = product.currentPrice(); }

      if (product.currentPrice() > maxPrice) { maxPrice = product.currentPrice(); }
    });

    return { min: minPrice, max: maxPrice };
  }
);

export const getActiveDesignPriceRange = createSelector(
  ({state}) => getActiveDesignProducts({state}),
  (products) => {
    let productArray = Object.values(products);
    let minPrice = -1;
    let maxPrice = -1;

    productArray.forEach(product => {
      if (product.currentPrice() < minPrice || minPrice < 0) { minPrice = product.currentPrice(); }

      if (product.currentPrice() > maxPrice) { maxPrice = product.currentPrice(); }
    });

    return { min: minPrice, max: maxPrice };
  }
);

export const getProductsSortedByPrice = createSelector(
  ({ state }) => extractFrom(state).products,
  (products) => {
    // sort by price, ascending
    return quicksort(Object.values(products), (a, b) => {
      return a.currentPrice() - b.currentPrice();
    });
  }
);

export const getActiveDesignProductsSortedByPrice = createSelector(
  ({ state }) => getActiveDesignProducts({state}),
  (products) => {
    // sort by price, ascending
    return quicksort(Object.values(products), (a, b) => {
      return a.currentPrice() - b.currentPrice();
    });
  }
);

export const getHeightRange = ({ state }) => {
  const { products } = extractFrom(state);
  let productArray = Object.values(products);
  let minHeight = -1;
  let maxHeight = -1;

  productArray.forEach(product => {
    if (product.hasHeight() && (product.height() < minHeight || minHeight < 0)) { minHeight = product.height(); }

    if (product.height() > maxHeight) { maxHeight = product.height(); }
  });

  return { min: minHeight, max: maxHeight };
};

export const getWidthRange = ({ state }) => {
  const { products } = extractFrom(state);
  let productArray = Object.values(products);
  let minWidth = -1;
  let maxWidth = -1;

  productArray.forEach(product => {
    if (product.hasWidth() && (product.width() < minWidth || minWidth < 0)) { minWidth = product.width(); }

    if (product.width() > maxWidth) { maxWidth = product.width(); }
  });

  return { min: minWidth, max: maxWidth };
};

export const getDepthRange = ({ state }) => {
  const { products } = extractFrom(state);
  let productArray = Object.values(products);
  let minDepth = -1;
  let maxDepth = -1;

  productArray.forEach(product => {
    if (product.hasDepth() && (product.depth() < minDepth || minDepth < 0)) { minDepth = product.depth(); }

    if (product.depth() > maxDepth) { maxDepth = product.depth(); }
  });

  return { min: minDepth, max: maxDepth };
};

export const getPurchasedProductIds = (state) => {
  let { purchasedProductIds } = extractFrom(state);
  return purchasedProductIds;
};

export const getIsStudioMessengerEnabled = (state) => {
  const space = getActiveSpace({ state });
  return space && space.isMessengerEligible();
};

/**
 * Determines whether or not this is a shared space view.
 * For now, this is completely determined by the URL path.
 */
export const getIsSharedSpace = () => {
  return window.location.pathname.indexOf('/studio/shared') === 0;
};

/**
* Determines whether or not user is in /shop.
*/
export const getIsShopOpen = () => {
  return window.location.pathname.includes('/shop');
};

export const getIsCartOpen = () => {
  return window.location.pathname.includes('/cart');
};

export const getIsCartAndPDPOpen = () => {
  return window.location.pathname.includes('/cart/product');
};

export const getSelectedHotspotProduct = (state) => {
  return extractFrom(state).selectedHotspotProduct;
};

export const getCurrentSelectedHotspotProduct = () => (dispatch, getState) => {
  return getSelectedHotspotProduct(getState());
};

export const getPDPHotspotProduct = (state) => {
  const info = extractFrom(state).selectedPDPHotspotProductInfo;
  return get(info, 'product', null);
};

export const getPDPHotspotUid = (state) => {
  const info = extractFrom(state).selectedPDPHotspotProductInfo;
  return get(info, 'hotspotUid', null);
};

export const getCurrentPDPHotspotProduct = () => (dispatch, getState) => {
  return getPDPHotspotProduct(getState());
};

export const getCurrentPDPHotspotUid = () => (dispatch, getState) => {
  return getPDPHotspotUid(getState());
};

export const getSelectedHotspotUid = (state) => {
  return extractFrom(state).selectedHotspotUid;
};

/**
 * This is true if the alternatives we're looking at were from a hotspot PDP click
 */
export const getAreAlternativesFromHotspot = (state) => {
  return extractFrom(state).alternativesFromHotspot;
};

export const getCurrentSelectedHotspotUid = () => (dispatch, getState) => {
  return getSelectedHotspotUid(getState());
};

/** Returns all the revisions currently in state indexed by ID. */
export const getIndexedRevisions = state => {
  return extractFrom(state).revisions;
};

export const getRevisionFor = ({ revisionUuid, state }) => {
  const revisions = getIndexedRevisions(state);
  const revision = revisions[revisionUuid] || null;
  return revision;
};

/** Returns the active revision or null. */
export const getActiveRevision = (state) => {
  const design = getActiveDesign({ state });
  if (!design) { return null; }
  const revisionUuid = design.revisionUuid();
  return revisionUuid
    ? getRevisionFor({ revisionUuid, state })
    : null;
};

export const getRenderFor = ({ renderUid, state }) => {
  const data = extractFrom(state);
  const render = data.renders[renderUid] || null;
  return render;
};

export const getRenders = (state) => extractFrom(state).renders;

/** Returns the show lookbook flag */
export const getShowLookbook = (state) => extractFrom(state).showLookbook;

/** Returns the first display render hotspot instance id matching given product
 *  for a given design. Uses getDisplayRenders logic to cycle through revision history
 *  of display renders stored in redux.
**/
export const getHotspotForProduct = ({
  design = null,
  productId = 0
}) => (dispatch) => {
  const displayRenders = dispatch(getCurrentDisplayRendersFor({ design }));
  for (const render of displayRenders) {
    const hotspots = get(render, 'hotspots', []);
    const matchingHotspot = hotspots.find((hotspot) => {
      const hotspotProductId = hotspot.productId || hotspot.product_id || null;
      return hotspotProductId === productId;
    });

    if (matchingHotspot) {
      return matchingHotspot.instanceId || matchingHotspot.instance_id || '';
    }
  }
  return '';
};

/**
 *  Returns all of display render hotspot instance ids matching given product
 *  for a given design. Uses getDisplayRenders logic to cycle through revision history
 *  of display renders stored in redux.
**/
export const getAllHotspotsForProduct = ({
  design = null,
  productId = 0
}) => (dispatch) => {
  const displayRenders = dispatch(getCurrentDisplayRendersFor({ design }));
  let allHotspots = [];
  for (const render of displayRenders) {
    const hotspots = get(render, 'hotspots', []);
    const matchingHotspots = hotspots.filter((hotspot) => {
      const hotspotProductId = hotspot.productId || hotspot.product_id || null;
      return hotspotProductId === productId;
    });

    if (matchingHotspots) {
      allHotspots = [...allHotspots, ...matchingHotspots.map(hotspot => hotspot.instanceId || hotspot.instance_id || '')];
    }
  }

  return unique(allHotspots);
};

export const getCurrentDisplayRendersFor = ({ design }) => (_, getState) => {
  return getDisplayRendersFor({ design, state: getState() });
};

/**
 * Determines which renders are available for a given design.
 * Takes into account design revisions.
 */
export const getDisplayRendersFor = createSelector(
  ({ design, state }) => getDesignFor({ designId: (design.id() || 0), state }),
  ({ design, state }) => getRenders(state),
  (design, revisionRenders) => {
    if (!design) { return []; }

    const renders = design.renders();
    if (!renders) { return []; }

    const revisionHistory = design.revisionHistory();

    const displayRenders = renders.filter(render => !!render).map(render => {
      // Handle feedback renders.
      if (`${render.id}`.includes('feedback')) {
        return render;
      }

      // Walk through revision history and try to find
      // an available render.
      for (let revisionUuid of revisionHistory) {
        const renderUid = Render.createRenderUid({
          revisionUuid,
          cameraName: render.camera_id
        });

        const revisionRender = revisionRenders[renderUid];
        if (!revisionRender) {
          continue; // walk back to prior revision
        }

        // Determine the render variant.
        // If there is a beauty variant ready then prefer it,
        // otherwise try and use the draft variant.
        let variant = null;

        const beauty = revisionRender.beautyVariant();
        const draft = revisionRender.draftVariant();
        let revisionPath = 'fields.variants.draft.fields.hotspots';
        if (beauty && beauty.isFinished()) {
          revisionPath = 'fields.variants.beauty.fields.hotspots';
          variant = beauty;
        } else if (draft && draft.isFinished()) {
          variant = draft;
        }

        if (!variant) {
          continue; // walk back to prior revision
        }

        // If the revision has hotspots grab them to replace the original render's hotspots
        const revisionHotspots = get(revisionRender, revisionPath, []);

        // Undefined if there are none
        let newHotspots;
        // Setup for the spread into the original render
        if (revisionHotspots) {
          newHotspots = { hotspots: revisionHotspots };
        }

        return {
          ...render,
          ...newHotspots,
          imagefile: variant.imageUrl()
        };
      }

      // Handle missing image URL.
      if (!render.imagefile) { return null; }

      // If there are no renders available in the revision history,
      // then fallback to the base render image.
      return render;
    }).filter(render => !!render);

    return displayRenders;
  }
);

export const getDesignSelectorActive = ({ state }) => extractFrom(state).designSelectorActive;

export const getProductViewActive = (state) => extractFrom(state).productViewActive;

export const getPackagesData = (state) => extractFrom(state).packagesData;

export const getCurrentPackageData = () => (dispatch, getState) => {
  return getPackagesData(getState());
};

export const getLayoutModalActive = (state) => {
  return extractFrom(state).layoutModalActive;
};

export const getLocallySelectedLayoutId = (state) => {
  return extractFrom(state).locallySelectedLayoutId;
};

export const getBannerZindex = ({ state }) => {
  return extractFrom(state).bannerContainerZindex;
};
